/**
 * NOTE: Currently the case where `storeRating` is null is just the Promobit
 * store page. If this case becomes more common, code split with next's
 * `dynamic`.
 */
import PropTypes from 'prop-types';
import { forwardRef } from 'react';

import Heading from 'shopper/components/Heading';
import Link from 'shopper/components/Link';
import Text from 'shopper/components/Text';

import StarsRating from 'components/StarsRating';

import { sendEvent } from 'lib/gtag';

import { useUser } from 'providers/UserProvider';

import { postStoreRating } from 'services/store';

import APP from 'constants/app';
import SOCIAL_MEDIA from 'constants/socialMedia';

const getRatingCountText = (count) =>
  count === 1 ? 'Avaliação' : 'Avaliações';

const StoreDetails = forwardRef(
  (
    {
      gaEventCategory,
      storeAddress,
      storeContacts,
      storeDescription,
      storeDomain,
      storeDomainEnriched,
      storeFacebookPage,
      storeName,
      storeRating,
      storeRatingCount,
      storeSlug = null,
    },
    ref
  ) => {
    const { isLoggedIn, runCallbackIfLoggedIn } = useUser();

    const onRatingClick = (rating) => {
      sendEvent({
        action: isLoggedIn ? 'rate_store' : 'rate_store_unlogged',
        category: gaEventCategory,
      });
      runCallbackIfLoggedIn(postStoreRating, { storeSlug, rating });
    };

    return (
      <article ref={ref} className="flex flex-col gap-y-4">
        {(storeRating || storeRating === 0) && (
          <section className="flex flex-col gap-y-3">
            <Heading as="h2" size="size6">
              Resumo de {storeName}
            </Heading>
            <StarsRating
              ariaLabelWhenRated={`A loja ${storeName} é avaliada em ${storeRating}`}
              rating={storeRating}
              size="size2"
              withRatingAverage
              onRatingClick={onRatingClick}
            />
            <Text className="text-neutral-high-500" size="size2">
              {storeRatingCount} {getRatingCountText(storeRatingCount)}
            </Text>
          </section>
        )}
        <section className="flex flex-col gap-y-3">
          <Heading as="h2" size="size6">
            Informações sobre {storeName}
          </Heading>
          {/* "loja" seems like a default value which makes no sense to show */}
          {storeDescription && storeDescription.toLowerCase() !== 'loja' && (
            <Text className="text-balance text-neutral-high-500" size="size2">
              {storeDescription}
            </Text>
          )}
          {storeAddress && (
            <Text className="text-balance text-neutral-high-500" size="size2">
              {storeAddress}
            </Text>
          )}
          {storeContacts && (
            <Text className="text-balance text-neutral-high-500" size="size2">
              {storeContacts}
            </Text>
          )}
          {storeDomain && storeDomain !== APP.SHORTENED_URL && (
            <Link
              href={storeDomainEnriched || storeDomain}
              rel="nofollow"
              size="size2"
              target="_blank"
            >
              {storeDomain}
            </Link>
          )}
          {storeFacebookPage && (
            <Link
              href={storeFacebookPage}
              rel="nofollow"
              size="size2"
              target="_blank"
            >
              {SOCIAL_MEDIA.FACEBOOK.TITLE}
            </Link>
          )}
        </section>
      </article>
    );
  }
);

StoreDetails.displayName = 'StoreDetails';

StoreDetails.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  storeAddress: PropTypes.string,
  storeContacts: PropTypes.string,
  storeDescription: PropTypes.string,
  storeDomain: PropTypes.string,
  storeDomainEnriched: PropTypes.string,
  storeFacebookPage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  storeRating: PropTypes.number,
  storeRatingCount: PropTypes.number,
  storeSlug: PropTypes.string,
};

export default StoreDetails;
